import {DataTable} from "../DataTable/DataTable";
import {GeofenceEntity} from "@ots-frontend/ots-data";
import {useTranslation} from "react-i18next";
import {usePagination} from "../DataTable/usePagination";
import {useGeofenceDataTableFilter} from "../GeofenceDataTable/useGeofenceDataTableFilter";
import {useDataTableSorter} from "../DataTable/useDataTableSorter";
import {EntityDataTableProps} from "../EntityDataTable/EntityDataTable";
import {type ReactNode, useMemo} from "react";
import {DataTableColumn} from "mantine-datatable";
import {sortBy} from "lodash";
import {FormattedDatabaseCode} from "../../text/FormattedDatabaseCode/FormattedDatabaseCode";

const EMPTY_ARRAY: GeofenceEntity[] = []

export type BaseGeofenceDataTableProps = EntityDataTableProps<GeofenceEntity> & {
  renderActions: (record: GeofenceEntity, index: number) => ReactNode
}

export const BaseGeofenceDataTable = (props: BaseGeofenceDataTableProps) => {
  const {
    records: geofences = EMPTY_ARRAY,
    recordsPerPageOptions,
    renderActions,
    ...rest
  } = props

  const {t} = useTranslation(["common"]);

  const {filteredRecords, filterConfig} = useGeofenceDataTableFilter({
    records: geofences
  })

  const sortConfig = useMemo(() => {
    return {
      "radius": {
        sortAll: (records, columnAccessor) => {
          return sortBy(records, [function (record) {
            return record.name?.toLowerCase()
          }])
        }
      },
      "triggerType": {
        sortAll: (records, columnAccessor) => {
          return sortBy(records, [function (record) {
            return record.description?.toLowerCase()
          }])
        }
      },
    }
  }, [])

  const {
    sortedRecords,
    sortStatus,
    onSortStatusChange,
  } = useDataTableSorter({
    records: filteredRecords,
    sortStatus: {columnAccessor: 'triggerType', direction: 'asc'},
    sortConfig: sortConfig
  })

  const pagination = usePagination({
    data: sortedRecords,
    recordsPerPageOptions: recordsPerPageOptions,
  });

  const columns = useMemo(() => {
    const values: DataTableColumn<GeofenceEntity>[] = [
      {
        title: t("common.entity.geofence.table.column.trigger", "Trigger"),
        accessor: 'trigger',
        sortable: true,
        render: ({triggerType}) => (
          <FormattedDatabaseCode value={triggerType}/>
        )
      },
      {
        title: t("common.entity.geofence.table.column.latitude", "Lat."),
        accessor: 'latCoord',
        sortable: true,
      },
      {
        title: t("common.entity.geofence.table.column.longitude", "Long."),
        accessor: 'longCoord',
        sortable: true,
      },
      {
        title: t("common.entity.geofence.table.column.radius", "Radius"),
        accessor: 'radius',
        sortable: true,
      },
      {
        title: '',
        accessor: "actions",
        width: 100,
        textAlignment: 'right',
        render: renderActions,
      }
    ]

    return values
  }, [filterConfig, t, renderActions])

  return (
    <DataTable
      {...rest}
      {...(pagination ?? {})}
      sortStatus={sortStatus}
      onSortStatusChange={onSortStatusChange}
      columns={columns}
    />
  )
}
